import { useState, useEffect, useCallback } from "react";
import { useGetChipExportData } from "../api";
import { DateTime } from "luxon";

const DownloadDataAVR = ({ ids, needsScanData, experimentName }) => {
  //state variables

  const [data, setData] = useState([]);
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const [shouldSaveData, setShouldSaveData] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  //fetching scan data from database
  const scanQueries = useGetChipExportData(needsScanData ? ids : []);

  //updating data with scan data from database
  useEffect(() => {
    if (
      needsScanData &&
      !isDataUpdated &&
      scanQueries.length > 0 &&
      scanQueries.every((q) => q.isSuccess) &&
      isButtonClicked
    ) {
      setData(
        scanQueries.map((query) => {
          return query.data;
        })
      );
      setIsDataUpdated(true);
      setShouldSaveData(true);
    }
  }, [scanQueries, needsScanData, isDataUpdated]);

  //calls saveData function when data is updated
  useEffect(() => {
    if (isDataUpdated && shouldSaveData) {
      saveData();
      setShouldSaveData(false);
      setIsButtonClicked(false);
    }
  }, [isDataUpdated, shouldSaveData]);

  const formatData = useCallback(() => {
    const headers = [
      "Experiment",
      "Chip",
      "Round",
      "Spot",
      "Date",
      "Time",
      "Scan Type",
      "Analyte",
      "Findings",
    ];
    const sampleInfo = data?.map((d) =>
      d.tests
        ?.map((scan) => {
          let localDate = DateTime.fromISO(scan.date).setZone("local");
          let findings =
            scan.findings?.map((f) => f.substance).join(", ") || "N/A";
          return [
            experimentName,
            d.chipNum,
            scan.roundId,
            scan.spotId,
            localDate.toFormat("yyyy-MM-dd"),
            localDate.toFormat("HH:mm:ss"),
            scan.scanType,
            d.analyte,
            findings,
          ].join("\t");
        })
        .join("\n")
    );

    const infoData = headers.join("\t") + "\n" + sampleInfo.join("\n");

    const scanData =
      "\t" +
      (data[0]?.tests[0]?.wavenumber_plot || []).join("\t") +
      "\n" +
      data
        .map((d) =>
          d.tests
            ?.map((scan) => {
              return (
                "Chip" +
                d.chipNum +
                "Round" +
                scan.roundId +
                "Spot" +
                scan.spotId +
                "\t" +
                (scan.interpolatedRawDark || []).join("\t")
              );
            })
            .join("\n")
        )
        .join("\n");
    return { scanData, infoData };
  }, [data]);

  const saveAs = useCallback((blob, fileName) => {
    if (!(blob instanceof Blob)) {
      blob = new Blob([blob], { type: "text/tab-separated-values" });
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
  }, []);

  const fetchData = useCallback(async () => {
    if (needsScanData) {
      try {
        await Promise.all(
          scanQueries.map((query) => {
            if (query.refetch) {
              return query.refetch();
            }
            return Promise.resolve();
          })
        );
        setIsDataUpdated(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  }, [needsScanData, scanQueries]);

  const saveData = useCallback(() => {
    const { scanData, infoData } = formatData();
    saveAs(scanData, `data_${experimentName}.txt`);
    saveAs(infoData, `info_${experimentName}.txt`);
  }, [formatData, saveAs]);

  return (
    <div className='d-flex justify-content-center'>
      <button
        className='btn btn-outline-primary'
        onClick={() => {
          setIsButtonClicked(true);
          needsScanData ? fetchData() : saveData();
        }}
      >
        Download Data
      </button>
    </div>
  );
};

export default DownloadDataAVR;
